import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import {
  Button,
  Badge,
  StatCard,
  PageHeader,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  EmptyState,
  Spinner,
} from '@theonefamily/ui';
import { Truck, Plus, Activity, Wrench, Gauge } from 'lucide-react';

interface Vehicle {
  id: string;
  name: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  status: 'active' | 'maintenance' | 'retired' | 'sold';
  assignedTo: string | null;
  mileage: number;
}

interface VehicleListData {
  vehicles: Vehicle[];
  total: number;
  active: number;
  inMaintenance: number;
  avgMileage: number;
}

const STATUS_VARIANT: Record<string, 'success' | 'destructive' | 'secondary' | 'warning'> = {
  active: 'success',
  maintenance: 'destructive',
  retired: 'secondary',
  sold: 'warning',
};

export default function VehicleListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useApi<VehicleListData>('/api/vehicles');

  const vehicles = data?.vehicles ?? [];
  const total = data?.total ?? 0;
  const active = data?.active ?? 0;
  const inMaintenance = data?.inMaintenance ?? 0;
  const avgMileage = data?.avgMileage ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vehicles"
        description="Manage your fleet vehicles"
        actions={
          <Button>
            <Plus size={14} />
            Add Vehicle
          </Button>
        }
      />

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Truck} label="Total" value={total} />
        <StatCard icon={Activity} label="Active" value={active} accent="success" />
        <StatCard icon={Wrench} label="In Maintenance" value={inMaintenance} accent="destructive" />
        <StatCard icon={Gauge} label="Avg Mileage" value={`${avgMileage.toLocaleString()} mi`} />
      </div>

      {/* Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : vehicles.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="No vehicles found"
            description="Add your first vehicle to get started."
            action={{ label: 'Add Vehicle', onClick: () => {} }}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vehicle</TableHead>
                <TableHead>Make / Model</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Plate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead className="text-right">Mileage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.map((v) => (
                <TableRow
                  key={v.id}
                  onClick={() => navigate(`/vehicles/${v.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell className="font-medium">{v.name}</TableCell>
                  <TableCell>{v.make} {v.model}</TableCell>
                  <TableCell>{v.year}</TableCell>
                  <TableCell className="font-mono text-xs">{v.licensePlate}</TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[v.status] ?? 'secondary'}>
                      {v.status.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell style={{ color: 'var(--muted-foreground)' }}>{v.assignedTo ?? '—'}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{v.mileage.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
